import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calculator,
  FileStack,
  Save,
  Sparkles,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useTemplates } from '../../features/templates/useTemplates';
import { DEFAULT_ESTIMATION_TEMPLATE_NAME } from '../../features/templates/defaultTemplates';
import { useEstimates } from '../../features/estimation/useEstimates';
import { useRooms } from '../../features/rooms/useRooms';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useTasks } from '../../features/tasks/useTasks';
import { draftFromTask } from '../../features/tasks/taskModel';
import {
  defaultInputs,
  evaluateTemplate,
  normalizeInputs,
} from '../../features/estimation/engine';
import type {
  Estimate,
  EstimateInputValue,
  EstimateInputs,
} from '../../features/estimation/types';
import type { FieldDef, Template } from '../../features/templates/types';
import { EstimateFieldInput } from './EstimateFieldInput';
import { OutputsPreview } from './OutputsPreview';

interface FieldGroup {
  name: string;
  fields: FieldDef[];
}

/** Groups fields by their optional `group`, preserving declaration order. */
function groupFields(fields: FieldDef[]): FieldGroup[] {
  const order: string[] = [];
  const byGroup = new Map<string, FieldDef[]>();
  for (const f of fields) {
    const g = f.group?.trim() || 'General';
    if (!byGroup.has(g)) {
      byGroup.set(g, []);
      order.push(g);
    }
    byGroup.get(g)!.push(f);
  }
  return order.map((name) => ({ name, fields: byGroup.get(name)! }));
}

/**
 * Estimate Wizard — pick a template, fill its dynamically-rendered fields,
 * live-preview the browser-computed outputs, then save as an Estimate document.
 * Optionally links the estimate to a room and to a task in the active room.
 *
 * Strict layering: reaches data only through feature hooks (useTemplates,
 * useEstimates, useRooms, useTasks) — never the api facade directly.
 */
export function EstimateWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Optional clone source passed via router state ("re-open" from detail view).
  const cloneRef = useRef<Estimate | null>(
    (location.state as { cloneFrom?: Estimate } | null)?.cloneFrom ?? null,
  );
  const cloneFrom = cloneRef.current;

  // Optional suggested inputs passed via router state (from a Knowledge Effort
  // Insight → "Use in estimate"). Template-agnostic: only keys that match the
  // chosen template's field schema are applied (normalizeInputs drops the rest).
  const suggestedRef = useRef<{
    inputs?: Record<string, EstimateInputValue>;
    from?: string;
    title?: string;
  } | null>(
    (() => {
      const s = location.state as {
        suggestedInputs?: Record<string, EstimateInputValue>;
        suggestedFrom?: string;
        suggestedTitle?: string;
      } | null;
      if (!s?.suggestedInputs) return null;
      return {
        inputs: s.suggestedInputs,
        from: s.suggestedFrom,
        title: s.suggestedTitle,
      };
    })(),
  );
  const suggested = suggestedRef.current;

  const templates = useTemplates();
  const estimates = useEstimates();
  const rooms = useRooms();
  const { activeRoomId } = useActiveRoom();
  const tasks = useTasks(); // scoped to the active room

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    cloneFrom?.templateId ?? '',
  );
  const [template, setTemplate] = useState<Template | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [inputs, setInputs] = useState<EstimateInputs>({});

  const [title, setTitle] = useState<string>(
    cloneFrom ? `Copy of ${cloneFrom.title}` : (suggested?.title ?? ''),
  );
  const [roomId, setRoomId] = useState<string>(
    cloneFrom?.roomId ?? activeRoomId ?? '',
  );
  // Tracks whether the user has explicitly picked a room; until then we keep the
  // room synced to the active room (which may hydrate after first render).
  const roomTouchedRef = useRef(false);
  const [linkTaskId, setLinkTaskId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Ensure the built-in default templates exist (idempotent) so a first-time
  // user always has something to estimate against.
  useEffect(() => {
    void templates.ensureDefaults();
  }, [templates.ensureDefaults]);

  // Keep the estimate scoped to the active room until the user explicitly picks
  // one. `activeRoomId` can hydrate asynchronously after the first render (fresh
  // load / deep link), so read it reactively here rather than only as initial
  // state — otherwise a late-arriving room id would never be applied.
  useEffect(() => {
    if (roomTouchedRef.current || cloneRef.current) return;
    if (activeRoomId && roomId !== activeRoomId) setRoomId(activeRoomId);
  }, [activeRoomId, roomId]);

  // Pre-select a sensible default template on first load when nothing is chosen
  // (not cloning): prefer the B+C+W estimation template, else the first one.
  const preselectRef = useRef(false);
  useEffect(() => {
    if (preselectRef.current) return;
    if (selectedTemplateId) {
      preselectRef.current = true;
      return;
    }
    if (templates.summaries.length === 0) return;
    const preferred = templates.summaries
      .filter((s) => s.name === DEFAULT_ESTIMATION_TEMPLATE_NAME)
      .sort((a, b) => b.version - a.version)[0];
    const chosen = preferred ?? templates.summaries[0];
    preselectRef.current = true;
    setSelectedTemplateId(chosen.id);
  }, [templates.summaries, selectedTemplateId]);

  // Load the full template document whenever the selection changes, then seed
  // the inputs (from the clone source if it matches, otherwise from defaults).
  useEffect(() => {    let cancelled = false;
    if (!selectedTemplateId) {
      setTemplate(null);
      setInputs({});
      setTemplateError(null);
      return;
    }
    setTemplateLoading(true);
    setTemplateError(null);
    templates
      .getById(selectedTemplateId)
      .then((t) => {
        if (cancelled) return;
        if (!t) {
          setTemplate(null);
          setInputs({});
          setTemplateError('Template not found.');
          return;
        }
        setTemplate(t);
        const clone = cloneRef.current;
        const cloneSeed =
          clone && clone.templateId === t.id ? clone.inputs : undefined;
        // Fall back to Knowledge-suggested inputs when there is no clone seed;
        // normalizeInputs keeps only keys present in this template's schema.
        const seed = cloneSeed ?? suggestedRef.current?.inputs;
        setInputs(seed ? normalizeInputs(t, seed) : defaultInputs(t));
      })
      .catch((e) => {
        if (cancelled) return;
        setTemplate(null);
        setTemplateError(
          e instanceof Error ? e.message : 'Failed to load template.',
        );
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTemplateId, templates.getById]);

  const templateOptions = useMemo<SelectOption[]>(() => {
    return templates.summaries
      .slice()
      .sort(
        (a, b) =>
          a.name.localeCompare(b.name) || b.version - a.version,
      )
      .map((s) => ({
        value: s.id,
        label: `${s.name} (v${s.version})`,
      }));
  }, [templates.summaries]);

  const roomOptions = useMemo<SelectOption[]>(
    () => rooms.summaries.map((r) => ({ value: r.id, label: r.name })),
    [rooms.summaries],
  );

  const taskOptions = useMemo<SelectOption[]>(
    () => tasks.tasks.map((t) => ({ value: t.id, label: t.title })),
    [tasks.tasks],
  );

  const groups = useMemo(
    () => groupFields(template?.fields ?? []),
    [template],
  );

  const evaluation = useMemo(
    () =>
      template
        ? evaluateTemplate(template, inputs)
        : { outputs: {}, errors: {} },
    [template, inputs],
  );

  const setField = (key: string, value: EstimateInputValue) =>
    setInputs((prev) => ({ ...prev, [key]: value }));

  const canSave = !!template && title.trim() !== '' && !saving;
  const showTaskLink = !!roomId && roomId === activeRoomId && taskOptions.length > 0;

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    try {
      const result = evaluateTemplate(template, inputs);
      const saved = await estimates.create({
        title: title.trim(),
        roomId: roomId || undefined,
        templateId: template.id,
        templateVersion: template.version,
        inputs,
        outputs: result.outputs,
      });

      // Optional: link the estimate onto a task in the active room.
      if (showTaskLink && linkTaskId) {
        const task = tasks.tasks.find((t) => t.id === linkTaskId);
        if (task) {
          try {
            await tasks.update(task, {
              ...draftFromTask(task),
              estimateId: saved.id,
            });
          } catch {
            // Non-fatal: the estimate is saved regardless of the task link.
            toast({
              tone: 'warning',
              title: 'Estimate saved, task link failed',
              description: 'Could not attach the estimate to the selected task.',
            });
          }
        }
      }

      toast({
        tone: 'success',
        title: 'Estimate saved',
        description: saved.title,
      });
      navigate(`/estimation/${encodeURIComponent(saved.id)}`);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        leftIcon={<ArrowLeft className="h-4 w-4" />}
        onClick={() => navigate('/estimation')}
      >
        Back
      </Button>
      <Button
        leftIcon={<Save className="h-4 w-4" />}
        loading={saving}
        disabled={!canSave}
        onClick={handleSave}
      >
        Save estimate
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        icon={<Calculator className="h-5 w-5" />}
        title={cloneFrom ? 'Re-open estimate' : 'New estimate'}
        description="Pick a template, fill its inputs, and preview the computed outputs."
        actions={headerActions}
      />

      {suggested?.from && (
        <div className="mb-6 flex items-center gap-2 rounded-xl border border-accent/30 bg-accent-soft/30 px-4 py-2.5 text-sm text-accent-fg">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span>
            Prefilled from a Knowledge effort insight for{' '}
            <span className="font-semibold">{suggested.from}</span>. Matching
            template fields are seeded with suggested values.
          </span>
        </div>
      )}

      {templates.loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading templates" />
        </div>
      ) : templates.summaries.length === 0 ? (
        <EmptyState
          icon={<FileStack className="h-6 w-6" />}
          title="No templates yet"
          description="Estimates are computed against a template's schema. Create a template first."
          action={
            <Button
              leftIcon={<FileStack className="h-4 w-4" />}
              onClick={() => navigate('/templates/new')}
            >
              Create a template
            </Button>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* Left: configuration + inputs */}
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Setup</CardTitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-4">
                <Select
                  label="Template"
                  placeholder="Select a template…"
                  options={templateOptions}
                  value={selectedTemplateId}
                  onChange={(e) => {
                    cloneRef.current = null; // manual change discards clone seed
                    setSelectedTemplateId(e.target.value);
                    setLinkTaskId('');
                  }}
                />
                <Input
                  label="Estimate title"
                  placeholder="e.g. Q3 platform build"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Room (optional)"
                    placeholder="No room"
                    options={roomOptions}
                    value={roomId}
                    onChange={(e) => {
                      roomTouchedRef.current = true;
                      setRoomId(e.target.value);
                      setLinkTaskId('');
                    }}
                  />
                  {showTaskLink && (
                    <Select
                      label="Link to task (optional)"
                      placeholder="Don't link"
                      options={taskOptions}
                      value={linkTaskId}
                      onChange={(e) => setLinkTaskId(e.target.value)}
                    />
                  )}
                </div>
              </CardBody>
            </Card>

            {templateLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner label="Loading template" />
              </div>
            ) : templateError ? (
              <EmptyState
                icon={<Calculator className="h-6 w-6" />}
                title="Couldn't load template"
                description={templateError}
              />
            ) : !template ? (
              <EmptyState
                icon={<Sparkles className="h-6 w-6" />}
                title="Choose a template to begin"
                description="Its fields will appear here for you to fill in."
              />
            ) : template.fields.length === 0 ? (
              <EmptyState
                icon={<Calculator className="h-6 w-6" />}
                title="This template has no fields"
                description="Add input fields to the template to estimate against it."
              />
            ) : (
              groups.map((group) => (
                <Card key={group.name}>
                  <CardHeader>
                    <CardTitle>{group.name}</CardTitle>
                  </CardHeader>
                  <CardBody className="grid gap-4 sm:grid-cols-2">
                    {group.fields.map((field) => (
                      <EstimateFieldInput
                        key={field.key}
                        field={field}
                        value={inputs[field.key] ?? ''}
                        onChange={(v) => setField(field.key, v)}
                      />
                    ))}
                  </CardBody>
                </Card>
              ))
            )}
          </div>

          {/* Right: live outputs */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent" />
                    Live outputs
                  </span>
                </CardTitle>
                {template && (
                  <Badge tone="neutral">
                    {template.name} v{template.version}
                  </Badge>
                )}
              </CardHeader>
              <CardBody>
                {template ? (
                  <OutputsPreview
                    defs={template.outputs ?? []}
                    result={evaluation}
                    compact
                  />
                ) : (
                  <p className="text-sm text-zinc-500">
                    Outputs will compute live as you fill in a template.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default EstimateWizardPage;
